import { getDateFromUnix, trimDecimals } from '@einhaus/meteoric-helpers';

interface PriceEntrySetupSample {
    id: number;
    armedAt: number;
    eligibleAt: number;
    expiresAt: number;
    orderSide: string;
    orderType: string;
    status: string;
    terminalReason: string;
    fillPrice: number | null;
    fillMinuteTimestamp: number | null;
    fillEvidenceAvailableAt: number | null;
    fillEvidenceKind: string | null;
    diagnostic: string | null;
    targetRevisions: {
        revision: number;
        targetPrice: number;
        availableAt: number;
        sourceCutoff: number;
        sourceIdentity: string;
    }[];
}

export interface PriceEntrySetupSummaryValue {
    total: number;
    outcomes: { status: string; terminalReason: string; count: number }[];
    samples: PriceEntrySetupSample[];
    sampleLimit: number;
}

const formatTimestamp = (timestamp: number | null) =>
    timestamp === null ? '—' : `${getDateFromUnix(timestamp, 'ymdhm', 'America/New_York')} ET`;
const humanize = (value: string) => value.replaceAll('_', ' ');

const PriceEntrySetupSummary = ({ summary }: { summary: PriceEntrySetupSummaryValue | null | undefined }) => {
    if (!summary || summary.total === 0) return null;
    return (
        <div className="mt-3 rounded border border-zinc-700 bg-zinc-900/60 p-4">
            <h4 className="mb-1 text-lg font-semibold text-zinc-100">Dynamic price-entry setups</h4>
            <p className="mb-3 text-xs text-zinc-400">
                {summary.total.toLocaleString()} setup{summary.total === 1 ? '' : 's'} evaluated with frozen targets and minute evidence.
                The table shows the {Math.min(summary.samples.length, summary.sampleLimit)} most recently armed setups.
            </p>
            <div className="mb-4 flex flex-wrap gap-2">
                {summary.outcomes.map((outcome) => (
                    <span
                        className="rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs"
                        key={`${outcome.status}:${outcome.terminalReason}`}
                    >
                        {humanize(outcome.terminalReason)}: {outcome.count.toLocaleString()}
                    </span>
                ))}
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-xs text-zinc-200">
                    <thead>
                        <tr className="text-left text-zinc-400">
                            <th className="border-b border-zinc-700 p-2">Setup</th>
                            <th className="border-b border-zinc-700 p-2">Armed / eligible / expiry</th>
                            <th className="border-b border-zinc-700 p-2">Target</th>
                            <th className="border-b border-zinc-700 p-2">Outcome</th>
                            <th className="border-b border-zinc-700 p-2">Fill evidence</th>
                        </tr>
                    </thead>
                    <tbody>
                        {summary.samples.map((setup) => {
                            const target = setup.targetRevisions.at(-1);
                            return (
                                <tr className="align-top" key={setup.id}>
                                    <td className="border-b border-zinc-800 p-2 font-mono">#{setup.id}</td>
                                    <td className="border-b border-zinc-800 p-2 whitespace-nowrap">
                                        <div>{formatTimestamp(setup.armedAt)}</div>
                                        <div>{formatTimestamp(setup.eligibleAt)}</div>
                                        <div>{formatTimestamp(setup.expiresAt)}</div>
                                    </td>
                                    <td className="border-b border-zinc-800 p-2">
                                        <div className="font-semibold text-amber-200">
                                            {target ? `$${trimDecimals(target.targetPrice)}` : 'Unavailable'} · {setup.orderSide}{' '}
                                            {setup.orderType}
                                        </div>
                                    {target && (
                                        <>
                                            <div className="max-w-72 truncate text-zinc-500" title={target.sourceIdentity}>
                                                r{target.revision} · {target.sourceIdentity}
                                            </div>
                                            <div className="text-zinc-500">Available {formatTimestamp(target.availableAt)}</div>
                                            <div className="text-zinc-500">Source cutoff {formatTimestamp(target.sourceCutoff)}</div>
                                        </>
                                    )}
                                    </td>
                                    <td className="border-b border-zinc-800 p-2">
                                        <div>{setup.status}</div>
                                        <div className="text-zinc-400">{humanize(setup.terminalReason)}</div>
                                        {setup.diagnostic && <div className="max-w-72 text-zinc-500">{setup.diagnostic}</div>}
                                    </td>
                                    <td className="border-b border-zinc-800 p-2 whitespace-nowrap">
                                        {setup.fillPrice === null ? (
                                            '—'
                                        ) : (
                                            <>
                                                <div>${trimDecimals(setup.fillPrice)}</div>
                                                <div>{humanize(setup.fillEvidenceKind ?? 'unknown')}</div>
                                                <div>Minute {formatTimestamp(setup.fillMinuteTimestamp)}</div>
                                                <div>Available {formatTimestamp(setup.fillEvidenceAvailableAt)}</div>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PriceEntrySetupSummary;
